/**
 * Sidebar Component - Navigation sidebar with role-based menu filtering
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01 / UI-PERMISSION-LIST-NOT-ENFORCED-01): the menu follows the permission map
 *                      the server sent for the CHOSEN company (hooks/usePermissions); no role prop; a company menu
 *                      appears only when the person has more than one company
 * v3.00 - 2026-05-05 - WOW-RAG-CONTENT-01d.2: add "All Insights" as first child of Insights menu, points at /dashboard/insights (discoverability fix for 01d.1's InsightsFeed page)
 * v2.99 - 2026-02-07 - Fix #1: Filter webhooks nav item by manageWebhooks permission
 * v2.98 - 2026-02-07 - Added Webhooks nav item under Config
 * v2.93 - 2026-02-07 - Mobile responsive: temporary drawer on mobile, permanent on desktop
 * v2.33 - 2026-01-31 - Added role-based menu item filtering
 */
import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import StorageIcon from '@mui/icons-material/Storage';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TuneIcon from '@mui/icons-material/Tune';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import WebhookIcon from '@mui/icons-material/Webhook';
import HistoryIcon from '@mui/icons-material/History'; // v3.94 - MISSED-ID-04 EDGS: audit trail
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // v4.06 - [174]: Data Quality nav
import { TOPBAR_HEIGHT } from './TopBar';
import { useTranslation } from 'react-i18next';
import { FormControl, MenuItem as SelectMenuItem, Select } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { getChosenCompany, setChosenCompany, logClientDecision } from '../../services/portalRequest';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    text: 'Home',
    icon: <HomeIcon />,
    path: '/dashboard',
  },
  {
    text: 'Data',
    icon: <StorageIcon />,
    path: '/dashboard/data',
    children: [
      { text: 'Data Sources', icon: <StorageIcon />, path: '/dashboard/data' },
      { text: 'Data Quality', icon: <VerifiedUserIcon />, path: '/dashboard/data/quality' }, // v4.06 - [174]
    ],
  },
  {
    text: 'Insights',
    icon: <InsightsIcon />,
    path: '/dashboard/insights',
    children: [
      { text: 'All Insights', icon: <InsightsIcon />, path: '/dashboard/insights' }, // v3.00 - WOW-RAG-CONTENT-01d.2: discoverability fix for InsightsFeed page
      { text: 'Analytics', icon: <AnalyticsIcon />, path: '/dashboard/insights/analytics' },
      { text: 'Reports', icon: <DescriptionIcon />, path: '/dashboard/insights/reports' },
      { text: 'Dashboards', icon: <DashboardIcon />, path: '/dashboard/insights/dashboards' },
    ],
  },
  {
    text: 'Config',
    icon: <SettingsIcon />,
    path: '/dashboard/config',
    children: [
      { text: 'Settings', icon: <TuneIcon />, path: '/dashboard/config/settings' },
      { text: 'Team', icon: <GroupIcon />, path: '/dashboard/config/team' },
      { text: 'Webhooks', icon: <WebhookIcon />, path: '/dashboard/config/webhooks' },
      { text: 'Billing', icon: <PaymentIcon />, path: '/dashboard/config/billing' },
      { text: 'Audit Trail', icon: <HistoryIcon />, path: '/dashboard/config/audit-trail' }, // v3.94 - MISSED-ID-04 EDGS
    ],
  },
];

// v4.28 - G10-B: which permission key guards which screen (names only; the answer comes from the server)
const MENU_PERMISSION_BY_PATH: Record<string, string> = {
  '/dashboard': 'viewDashboard',
  '/dashboard/data': 'viewConnections',
  '/dashboard/data/quality': 'viewConnections',
  '/dashboard/insights/analytics': 'viewAnalytics',
  '/dashboard/insights/reports': 'viewReports',
  '/dashboard/config/settings': 'viewSettings',
  '/dashboard/config/team': 'viewTeam',
  '/dashboard/config/webhooks': 'manageWebhooks',
  '/dashboard/config/billing': 'viewBilling',
};

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mobileOpen?: boolean;          // v2.93 - Mobile drawer open state
  onMobileClose?: () => void;    // v2.93 - Close mobile drawer
  isMobile?: boolean;            // v2.93 - Mobile breakpoint flag
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggleCollapse, mobileOpen = false, onMobileClose, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSections, setOpenSections] = useState<string[]>(['Data', 'Insights', 'Config']); // v4.06 - [174]: Data expanded by default
  const { t } = useTranslation();
  const { hasPermission, companies, activeTenantUuid, userId: userIdForChoice } = usePermissions();

  // v4.28 - G10-B: a menu entry is shown only when the server says the person may use that screen.
  // This table names which permission key guards which screen; the yes/no comes from /api/me, never from here.
  const filteredMenuItems = useMemo(() => {
    const allowed = (path: string): boolean => {
      const guard = MENU_PERMISSION_BY_PATH[path];
      return guard ? hasPermission(guard) : true;
    };
    return menuItems.map((item) => {
      if (item.children) {
        const filteredChildren = item.children.filter((child) => allowed(child.path));
        // If no children left after filtering, hide the parent too
        if (filteredChildren.length === 0) {
          return null;
        }
        return { ...item, children: filteredChildren };
      }
      return allowed(item.path) ? item : null;
    }).filter((item): item is MenuItem => item !== null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, companies]);

  const handleToggle = (text: string) => {
    if (collapsed) return;
    setOpenSections((prev) =>
      prev.includes(text) ? prev.filter((t) => t !== text) : [...prev, text]
    );
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const currentWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openSections.includes(item.text) && !collapsed;
    const active = isActive(item.path);

    return (
      <React.Fragment key={item.path}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? item.text : ''} placement="right" arrow>
            <ListItemButton
              onClick={() => {
                if (hasChildren && !collapsed) {
                  handleToggle(item.text);
                } else if (hasChildren && collapsed) {
                  navigate(item.children![0].path);
                  if (isMobile && onMobileClose) onMobileClose(); // v2.93
                } else {
                  navigate(item.path);
                  if (isMobile && onMobileClose) onMobileClose(); // v2.93
                }
              }}
              sx={{
                pl: collapsed ? 2 : 2 + depth * 2,
                py: 1.25,
                mx: 1,
                borderRadius: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                bgcolor: active && !hasChildren ? 'rgba(244, 162, 97, 0.2)' : 'transparent',
                '&:hover': {
                  bgcolor: active && !hasChildren ? 'rgba(244, 162, 97, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'text.onDark',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 'auto',
                  opacity: active && !hasChildren ? 1 : 0.8,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: active && !hasChildren ? 600 : 400,
                    }}
                    sx={{ color: 'text.onDark' }}
                  />
                  {hasChildren && (
                    <Box sx={{ color: 'text.onDark', opacity: 0.7 }}>
                      {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {hasChildren && !collapsed && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // v2.93 - Sidebar content (shared between permanent and temporary drawers)
  const sidebarContent = (
    <>
      {/* v4.28 - G10-B: company menu, only when the person belongs to more than one company */}
      {!collapsed && companies.length > 1 && (
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <FormControl fullWidth size="small">
            <Select
              value={getChosenCompany() || activeTenantUuid || ''}
              onChange={(e) => {
                const next = String(e.target.value);
                logClientDecision('company_switched', { from: getChosenCompany() || 'none', company: next });
                setChosenCompany(next, userIdForChoice);
                window.location.assign('/dashboard');
              }}
              inputProps={{ 'aria-label': t('teamAccess.company.menuLabel') }}
              sx={{ color: 'text.onDark', '.MuiSvgIcon-root': { color: 'text.onDark' } }}
            >
              {companies.map((company) => (
                <SelectMenuItem key={company.tenantUuid} value={company.tenantUuid}>
                  {company.companyName || t('teamAccess.company.unnamed')}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Collapse/Expand Toggle - hidden on mobile */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
            p: 1,
            borderBottom: 1,
            borderColor: 'rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* v1.0 - S13-U3: added aria-label for screen readers - 2026-06-28 */}
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              sx={{
                color: 'text.onDark',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <List sx={{ pt: 1 }}>
        {filteredMenuItems.map((item) => renderMenuItem(item))}
      </List>

      {/* v3.94 - MISSED-ID-04 EDGS: AI transparency link at sidebar footer */}
      {!collapsed && (
        <Box
          component="a"
          href="/ai-transparency"
          sx={{
            display: 'block',
            px: 2,
            py: 1,
            fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.45)',
            textDecoration: 'none',
            '&:hover': { color: 'rgba(255,255,255,0.75)' },
            mt: 'auto',
          }}
        >
          AI Transparency
        </Box>
      )}
    </>
  );

  // v2.93 - Mobile: temporary drawer (overlay), Desktop: permanent drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }} // Better mobile performance
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            bgcolor: 'background.sidebar',
            borderRight: 'none',
            mt: `${TOPBAR_HEIGHT}px`,
            height: `calc(100% - ${TOPBAR_HEIGHT}px)`,
            overflowX: 'hidden',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease-in-out',
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.sidebar',
          borderRight: 'none',
          mt: `${TOPBAR_HEIGHT}px`,
          height: `calc(100% - ${TOPBAR_HEIGHT}px)`,
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default Sidebar;
